import { useEffect, useState } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import GuarantorNudge from 'components/guarantorNudge'
import { useQuery } from 'react-query'
import posthog from 'posthog-js'
import Header from 'components/headers/bulletins'
import DotsLoading from 'components/loadings/dotsLoading'
import { ScoreExplainer, ScoreMeter } from 'components/memberScore'
import useGetMoneyPoolManagement from '../fundPage/hooks/useGetMoneyPoolManagement'
import {
    getAdminMemberRecord,
    getLoanCandidates,
    getMemberScore,
} from 'services/fund'
import { faFull, faPlain, longJalali } from 'utilities/helper/format'
import PortionGrid from './components/PortionGrid'
import LoanList from './components/LoanList'
import CapitalChart from './components/CapitalChart'
import {
    CapitalCard,
    GuaranteesCard,
    LoansSection,
    TransactionsCard,
} from './components/Blocks'
import { recordCopy } from './copy'
import { fundTransactionsOf } from 'modules/fund/tabs/memberFilter'

/**
 * کارنامهٔ عضو — one member's whole record with one fund.
 *
 * The old screen («سابقه مالی عضو») showed the manager exactly what the
 * member sees of themselves: a grey box of «label: value» lines. That
 * answers «what are this person's numbers». A manager is asking something
 * else — «what kind of member is this» — and no arrangement of the same
 * totals answers it. (pwa#36)
 *
 * pwa#61 gave the member the same page about themselves: «الان مدیر می‌بینه
 * کارنامه رو ولی خودش نمی‌بینه». Three things change when the reader is the
 * subject, and none of them is the data:
 *
 *   1. **Voice.** Every label was third person. See `copy.ts`.
 *   2. **The score's source.** رتبه and percentile are positions in the whole
 *      fund, so they need the fund-wide scan either way — but a manager
 *      reads it from `loan-candidates` (shared with the grant wizard, so
 *      moving between the two screens pays for the scan once), and a member
 *      reads `member-score`, which is the same ranking narrowed to their own
 *      row. `loan-candidates` hands back every member by name and phone
 *      number with their open loan request attached; that is a manager's
 *      worklist, not something to put in a member's bundle.
 *   3. **What is actionable.** «اعطای وام به این عضو» and the drill-through
 *      from a guarantee into that person's own کارنامه are manager powers.
 *      The backend refuses both for a member, so leaving them on screen
 *      would only offer a 403.
 *
 * Order is by decreasing urgency, so the two facts a manager acts on (the
 * score, and how much of the fund's money this person is holding) need no
 * scrolling.
 */
export default function MemberRecord() {
    const { moneyPool, poolship } = useParams()
    const navigate = useNavigate()
    const [explainerOpen, setExplainerOpen] = useState(false)

    // The requesting user's own poolship in this fund, filled server-side —
    // the only trustworthy answer to «is this page about me». NOT
    // localStorage `profile.id`, which is a MEMBER id and would be compared
    // against a poolship id, matching the wrong person without ever failing.
    const { moneyPoolManagement, isLoadingMoneypoolManagement } =
        useGetMoneyPoolManagement(moneyPool)
    const role = moneyPoolManagement?.poolship?.role
    const isManager = role === 'mgr' || role === 'own'
    const isSelf =
        !!moneyPoolManagement?.poolship?.id &&
        String(moneyPoolManagement.poolship.id) === String(poolship)
    // A manager opening their OWN record is both: they keep every manager
    // affordance, and they get read to in the second person, because the
    // person on the screen is them.
    const copy = recordCopy(isSelf ? 'self' : 'about')

    const record = useQuery(
        ['admin-member-record', moneyPool, poolship],
        getAdminMemberRecord,
        {
            onError: (error: any) =>
                posthog.capture('member_record_load_failed', {
                    fund_id: moneyPool,
                    poolship_id: poolship,
                    viewer: isSelf ? 'self' : 'manager',
                    status: error?.response?.status,
                }),
        }
    )

    // Deciding before the role is known would fire the manager's whole-fund
    // scan for a member, get a 403, and leave the score card missing on a
    // page that had every right to show it.
    const scoreSource = isManager ? 'candidates' : 'own'
    const candidates = useQuery(
        ['loan-candidates', moneyPool, false, false],
        getLoanCandidates,
        {
            staleTime: 5 * 60 * 1000,
            enabled:
                !isLoadingMoneypoolManagement && scoreSource === 'candidates',
        }
    )
    const ownScore = useQuery(
        ['member-score', moneyPool, poolship],
        getMemberScore,
        {
            staleTime: 5 * 60 * 1000,
            enabled: !isLoadingMoneypoolManagement && scoreSource === 'own',
        }
    )

    const scoreRow =
        scoreSource === 'candidates'
            ? candidates.data?.results?.find(
                  (row) => String(row.poolship_id) === String(poolship)
              )
            : ownScore.data?.result ?? undefined
    const scoreMeta =
        scoreSource === 'candidates' ? candidates.data : ownScore.data

    useEffect(() => {
        // `viewer` keeps the two readerships apart in the funnel. Without it
        // pwa#61 would silently fold member traffic into what has been a
        // manager-only metric since pwa#36, and the before/after would be
        // unreadable in both directions.
        if (isLoadingMoneypoolManagement) return
        posthog.capture('member_record_viewed', {
            fund_id: moneyPool,
            poolship_id: poolship,
            viewer: isSelf ? 'self' : 'manager',
        })
    }, [moneyPool, poolship, isSelf, isLoadingMoneypoolManagement])

    const data = record.data

    // `isLoading || !data` treats "errored" as "still loading", so a 403 or a
    // dropped connection used to spin forever. A member who reaches a record
    // that is not theirs has to be told, not left watching dots.
    if (record.isError) {
        const status = (record.error as any)?.response?.status
        // Refused OUR OWN record. The only way that happens is a backend
        // older than this bundle — the two deploy independently, and a
        // service worker can hold this one for days. «سابقه مالی عضو» is
        // still there and still correct, so fall back to it rather than
        // showing the member an error for a page they are entitled to.
        // ?legacy=1 stops that screen redirecting straight back here.
        if (isSelf && (status === 403 || status === 404)) {
            return (
                <Navigate
                    replace
                    to={`/fund/financial-history/${moneyPool}/${poolship}?legacy=1`}
                />
            )
        }
        return (
            <div className=" min-h-screen bg-canvas">
                <Header title={copy.pageTitle} />
                <div className=" m-3 rounded-2xl border border-stroke bg-white p-5 text-center">
                    <p className=" text-sm font-bold text-heading">
                        {status === 403 || status === 404
                            ? 'این کارنامه در دسترس شما نیست'
                            : 'کارنامه باز نشد'}
                    </p>
                    <p className=" mt-2 text-xs leading-6 text-text-secondary">
                        {status === 403 || status === 404
                            ? 'کارنامهٔ هر عضو را خودش و مدیر صندوق می‌بینند.'
                            : 'اتصال برقرار نشد. چند لحظه بعد دوباره امتحان کنید.'}
                    </p>
                    <button
                        type="button"
                        onClick={() =>
                            status === 403 || status === 404
                                ? navigate(-1)
                                : record.refetch()
                        }
                        className=" mt-4 min-h-[44px] rounded-[10px] bg-brand px-6 text-sm font-bold text-white"
                    >
                        {status === 403 || status === 404
                            ? 'بازگشت'
                            : 'تلاش دوباره'}
                    </button>
                </div>
            </div>
        )
    }

    return (
        <div className=" min-h-screen bg-canvas pb-6">
            <Header title={copy.pageTitle} />

            {/* The role is part of the render, not a decoration on it:
                until it is known, painting the body would show a member
                the grant CTA and live guarantee links for a frame, then
                take them away. Wait for both. */}
            {record.isLoading || isLoadingMoneypoolManagement || !data ? (
                <DotsLoading />
            ) : (
                <div className=" flex flex-col gap-3 p-3">
                    {/* identity */}
                    <section className=" rounded-2xl border border-stroke bg-white p-4 shadow-card">
                        <div className=" flex items-center gap-3">
                            <span className=" flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-brand-soft text-base font-bold text-brand-deep">
                                {(data.member_name || '؟').trim().charAt(0)}
                            </span>
                            <div className=" min-w-0 flex-1">
                                <p className=" truncate text-base font-bold text-heading">
                                    {data.member_name}
                                </p>
                                <p
                                    dir="ltr"
                                    className=" mt-0.5 text-right text-xs text-text-secondary"
                                >
                                    {data.phone_number}
                                </p>
                            </div>
                        </div>
                        <div className=" mt-3 grid grid-cols-2 gap-2.5 border-t border-divider pt-3">
                            <div>
                                <p className=" text-[11px] text-text-secondary">
                                    عضو از
                                </p>
                                <p className=" mt-0.5 text-xs font-bold text-text-body">
                                    {longJalali(data.joined_date)}
                                </p>
                            </div>
                            <div>
                                <p className=" text-[11px] text-text-secondary">
                                    {copy.guarantorLabel}
                                </p>
                                <p className=" mt-0.5 text-xs font-bold text-text-body">
                                    {data.guarantor?.member_name || 'ثبت نشده'}
                                </p>
                            </div>
                            {/* معرف — the old «سابقه مالی عضو» printed this
                                beside the ضامن and never collapsed it. pwa#61
                                sends members here instead of there, so
                                dropping a row they are used to seeing on
                                their own page would be a plain regression. */}
                            {!!data.recommender && (
                                <div>
                                    <p className=" text-[11px] text-text-secondary">
                                        {copy.recommenderLabel}
                                    </p>
                                    <p className=" mt-0.5 text-xs font-bold text-text-body">
                                        {data.recommender.member_name}
                                    </p>
                                </div>
                            )}
                        </div>
                    </section>

                    {/* score — the shared component, so #34-style changes land once */}
                    {scoreRow && (
                        <section className=" rounded-2xl border border-stroke bg-white p-4 shadow-card">
                            <p className=" text-sm font-bold text-text-body">
                                {copy.scoreTitle}
                            </p>
                            <div className=" mt-2 flex items-baseline gap-1.5">
                                <span className=" text-2xl font-bold text-heading">
                                    رتبهٔ {faPlain.format(scoreRow.rank)}
                                </span>
                                <span className=" text-xs text-text-secondary">
                                    از{' '}
                                    {faPlain.format(
                                        scoreMeta?.members_count ?? 0
                                    )}{' '}
                                    عضو
                                </span>
                            </div>
                            <div className=" mt-2.5">
                                <ScoreMeter percentile={scoreRow.percentile} />
                            </div>
                            <button
                                type="button"
                                onClick={() => {
                                    posthog.capture(
                                        'member_record_score_opened',
                                        {
                                            fund_id: moneyPool,
                                            poolship_id: poolship,
                                            viewer: isSelf ? 'self' : 'manager',
                                        }
                                    )
                                    setExplainerOpen(true)
                                }}
                                className=" mt-3 flex min-h-[44px] items-center gap-1.5 rounded-lg bg-canvas px-3.5 py-2.5 text-xs font-bold text-brand-deep"
                            >
                                جزئیات محاسبه
                            </button>
                        </section>
                    )}

                    <CapitalCard record={data} title={copy.capitalTitle} />
                    <CapitalChart
                        series={data.capital_series}
                        title={copy.chartTitle}
                        hint={copy.chartHint}
                    />
                    <PortionGrid
                        grid={data.portion_grid}
                        unpaidAmount={data.portions?.unpaid_amount ?? 0}
                    />

                    <LoansSection record={data} copy={copy}>
                        <LoanList loans={data.loan_list ?? []} />
                    </LoansSection>

                    <GuaranteesCard
                        data={data.guarantees}
                        moneypoolId={moneyPool}
                        copy={copy}
                        // A member cannot open anybody else's کارنامه — the
                        // backend 403s it — so the names stay names rather
                        // than becoming buttons that fail.
                        canOpenOthers={isManager}
                    />
                    <TransactionsCard
                        rows={data.transactions}
                        allLabel={copy.allTransactions}
                        // The card shows the newest fifteen; the fund's own
                        // list, narrowed to this member, holds the rest. A
                        // member reading their own record is let through too:
                        // the server only ever narrows, so they get at most
                        // their own rows.
                        onOpenAll={(source) => {
                            posthog.capture(
                                'member_record_transactions_opened',
                                {
                                    fund_id: moneyPool,
                                    poolship_id: poolship,
                                    viewer: isSelf ? 'self' : 'manager',
                                    source,
                                    shown_count: data.transactions.length,
                                }
                            )
                            navigate(
                                ...fundTransactionsOf(
                                    moneyPool as string,
                                    {
                                        poolship: Number(poolship),
                                        label: isSelf
                                            ? 'شما'
                                            : data.member_name,
                                    },
                                    source
                                )
                            )
                        }}
                    />

                    {isManager && (
                        <button
                            type="button"
                            onClick={() => {
                                posthog.capture('member_record_grant_clicked', {
                                    fund_id: moneyPool,
                                    poolship_id: poolship,
                                })
                                // «به این عضو» — so the wizard must not open
                                // on «به چه کسی؟». The candidate row is the
                                // same object step 1 would have handed it,
                                // taken from the query both screens share.
                                navigate(
                                    `/loans/management/grant/${moneyPool}`,
                                    {
                                        state: {
                                            borrower: scoreRow,
                                            source: 'member_record',
                                        },
                                    }
                                )
                            }}
                            className=" min-h-[44px] rounded-[10px] bg-brand p-3 text-sm font-bold text-white"
                        >
                            اعطای وام به این عضو
                        </button>
                    )}

                    {/* حق عضویت عقب افتاده: پیگیری از ضامن عضویت */}
                    {!isSelf && (
                        <div className=" -mx-4">
                            <GuarantorNudge
                                moneypoolId={moneyPool}
                                target={
                                    poolship
                                        ? {
                                              kind: 'portion',
                                              poolshipId: poolship,
                                          }
                                        : null
                                }
                                isManager={isManager}
                            />
                        </div>
                    )}
                </div>
            )}

            {scoreRow && (
                <ScoreExplainer
                    show={explainerOpen}
                    onHide={() => setExplainerOpen(false)}
                    candidate={scoreRow}
                    subjectName={copy.scoreSubject}
                    overdueMultiplier={scoreMeta?.overdue_multiplier ?? 3}
                    earlyMultiplier={scoreMeta?.early_multiplier ?? 3}
                    membersCount={scoreMeta?.members_count ?? 0}
                    inflationApplied={false}
                />
            )}
        </div>
    )
}
